import {
  CAlert,
  CButton,
  CCloseButton,
  CCol,
  COffcanvas,
  COffcanvasBody,
  COffcanvasHeader,
  COffcanvasTitle,
  CDatePicker,
  CForm,
  CFormFeedback,
  CFormInput,
  CFormLabel,
  CFormTextarea,
  CLoadingButton,
  CMultiSelect,
} from '@coreui/react-pro'
import React, { useRef, useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import AdsManagerService from 'src/service/AdsManagerService'
import ToastComponent from 'src/components/common/TaostComponent'
import ImageDropzone from 'src/components/common/ImageDropzone'
import { PLACEMENT_OPTIONS, formatDisplayDate, toApiDate } from './promoFormConfig'

const SUPPORTED_FORMATS = ['image/jpg', 'image/png', 'image/jpeg', 'image/gif']
const ACCEPTED_IMAGE_TYPES = 'image/jpeg,image/png,image/gif'
const MAX_IMAGE_SIZE = 5 * 1024 * 1024

const AddForm = ({ visible, onClose, fetchUsers }) => {
  const [loader, setLoader] = useState(false)
  const [errorMessages, setErrorMessages] = useState([])
  const [placementKey, setPlacementKey] = useState(0)
  const keepOpenRef = useRef(false)

  const imageSchema = (label) =>
    Yup.mixed()
      .nullable()
      .required(`${label} is required`)
      .test('size', 'File size too large, max file size is 5 Mb', (value) =>
        value ? value.size <= MAX_IMAGE_SIZE : true,
      )
      .test(
        'type',
        'Invalid file format selection',
        (value) => !value || SUPPORTED_FORMATS.includes(value?.type),
      )

  const validationSchema = Yup.object().shape({
    title: Yup.string().required('Title is required').max(50, '50 Character Limit is allowed.'),
    validity_date: Yup.string()
      .required('Validity date is required')
      .max(50, '50 Character Limit is allowed.'),
    placement: Yup.array()
      .min(1, 'Please select at least one placement')
      .required('Placement is required'),
    design_type: Yup.string()
      .required('Label is required')
      .max(50, '50 Character Limit is allowed.'),
    description: Yup.string()
      .required('Description is required')
      .test('word-limit', 'Maximum 50 words are allowed.', (value) => {
        if (!value) return true
        const wordCount = value
          .trim()
          .split(/\s+/)
          .filter((word) => word.length > 0).length
        return wordCount <= 50
      }),
    image: imageSchema('Compact image'),
    logo: imageSchema('Expanded image'),
  })

  const formik = useFormik({
    initialValues: {
      title: '',
      design_type: '',
      cta_text: '',
      phone_number: '',
      address: '',
      cta_url: '',
      primary_color: '',
      secondary_color: '',
      validity_date: '',
      description: '',
      schedule_start_date: null,
      schedule_end_date: null,
      image: null,
      logo: null,
      placement: [],
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: (data, actions) => {
      const formData = new FormData()
      formData.append('title', data.title)
      formData.append('promo_label', data.design_type)
      formData.append('cta_text', data.cta_text)
      formData.append('phone_number', data.phone_number)
      formData.append('address', data.address)
      formData.append('primary_color', data.primary_color)
      formData.append('secondary_color', data.secondary_color)
      formData.append('validity_date', data.validity_date)
      formData.append('cta_url', data.cta_url)
      // FormData turns null into the string "null", so send blanks as ''.
      formData.append('schedule_start_date', data.schedule_start_date || '')
      formData.append('schedule_end_date', data.schedule_end_date || '')
      formData.append('image', data.image)
      formData.append('logo', data.logo)
      formData.append('description', data.description)
      // save-ads plucks `value` off each option, so the raw selection is what it expects.
      formData.append('placement', JSON.stringify(data.placement))

      setLoader(true)
      AdsManagerService.saveAd(formData)
        .then((res) => {
          if (res.status === 200) {
            setErrorMessages([])
            fetchUsers?.()
            ToastComponent(res.message, 'success')
            resetFields(actions)
            if (!keepOpenRef.current) {
              onClose()
            }
          } else {
            const errors =
              res.errors && Array.isArray(res.errors)
                ? res.errors
                : [res.message || 'Something went wrong.']
            setErrorMessages(errors)
          }
          setLoader(false)
        })
        .catch(() => {
          ToastComponent('Something went wrong. Please try again.', 'error')
          setLoader(false)
        })
    },
  })

  const resetFields = (actions) => {
    actions.resetForm()
    // CMultiSelect keeps its own selection state, so remount it to clear the tags.
    setPlacementKey((key) => key + 1)
  }

  const handleClose = () => {
    resetFields(formik)
    setErrorMessages([])
    onClose()
  }

  const submitForm = (keepOpen) => {
    keepOpenRef.current = keepOpen
    formik.handleSubmit()
  }

  return (
    <COffcanvas
      placement="end"
      visible={visible}
      onHide={handleClose}
      backdrop
      style={{ width: 900 }}
    >
      <COffcanvasHeader>
        <div>
          <COffcanvasTitle>Add Promo Card</COffcanvasTitle>
          <div className="text-medium-emphasis small">
            Create a promo card and choose where it appears in your games.
          </div>
        </div>
        <CCloseButton className="text-reset" onClick={handleClose} />
      </COffcanvasHeader>
      <COffcanvasBody className="d-flex flex-column">
        {errorMessages.length > 0 && (
          <div className="error-list">
            {errorMessages.map((error, index) => (
              <CAlert color="danger" key={index}>
                {error}
              </CAlert>
            ))}
          </div>
        )}
        <CForm className="row g-3 flex-grow-1 align-content-start" onSubmit={formik.handleSubmit}>
          <CCol xs={12}>
            <h6 className="fw-bold mb-0">Required Details</h6>
          </CCol>
          <CCol xs={6}>
            <CFormLabel htmlFor="title">Title *</CFormLabel>
            <CFormInput
              placeholder="Title"
              className={
                'form-control' + (formik.errors.title && formik.touched.title ? ' is-invalid' : '')
              }
              value={formik.values.title}
              onChange={formik.handleChange}
              id="title"
              name="title"
            />
            {formik.errors.title && formik.touched.title && (
              <CFormFeedback invalid>{formik.errors.title}</CFormFeedback>
            )}
          </CCol>
          <CCol xs={6}>
            <CFormLabel htmlFor="design_type">Label *</CFormLabel>
            <CFormInput
              placeholder="Label"
              className={
                'form-control' +
                (formik.errors.design_type && formik.touched.design_type ? ' is-invalid' : '')
              }
              value={formik.values.design_type}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              id="design_type"
              name="design_type"
            />
            {formik.errors.design_type && formik.touched.design_type && (
              <CFormFeedback invalid>{formik.errors.design_type}</CFormFeedback>
            )}
          </CCol>
          <CCol xs={12}>
            <CFormLabel htmlFor="description">Details *</CFormLabel>
            <CFormTextarea
              placeholder="Details"
              className={
                'form-control' +
                (formik.errors.description && formik.touched.description ? ' is-invalid' : '')
              }
              value={formik.values.description}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              id="description"
              name="description"
              rows={4}
            />
            {formik.errors.description && formik.touched.description && (
              <CFormFeedback invalid>{formik.errors.description}</CFormFeedback>
            )}
          </CCol>
          <CCol xs={6}>
            <CFormLabel htmlFor="image">Compact Image *</CFormLabel>
            <ImageDropzone
              accept={ACCEPTED_IMAGE_TYPES}
              hint="900 x 820 px (mobile). JPG, PNG or GIF up to 5MB."
              minHeight={120}
              file={formik.values.image}
              error={formik.touched.image ? formik.errors.image : null}
              onChange={(selected) => {
                formik.setTouched({ ...formik.touched, image: true })
                formik.setFieldValue('image', selected)
              }}
            />
          </CCol>
          <CCol xs={6}>
            <CFormLabel htmlFor="logo">Expanded Image *</CFormLabel>
            <ImageDropzone
              accept={ACCEPTED_IMAGE_TYPES}
              hint="1200 x 400 px (mobile). JPG, PNG or GIF up to 5MB."
              minHeight={120}
              file={formik.values.logo}
              error={formik.touched.logo ? formik.errors.logo : null}
              onChange={(selected) => {
                formik.setTouched({ ...formik.touched, logo: true })
                formik.setFieldValue('logo', selected)
              }}
            />
          </CCol>
          <CCol xs={6}>
            <CFormLabel htmlFor="validity_date">Validity *</CFormLabel>
            <CDatePicker
              locale="en-US"
              inputDateFormat={formatDisplayDate}
              placeholder="DD/MM/YYYY"
              date={formik.values.validity_date || null}
              onDateChange={(date) => formik.setFieldValue('validity_date', toApiDate(date))}
              onBlur={() => formik.setFieldTouched('validity_date', true)}
              className={
                formik.errors.validity_date && formik.touched.validity_date ? 'is-invalid' : ''
              }
            />
            {formik.errors.validity_date && formik.touched.validity_date && (
              <CFormFeedback invalid className="d-block">
                {formik.errors.validity_date}
              </CFormFeedback>
            )}
          </CCol>
          <CCol xs={6}>
            <CFormLabel htmlFor="placement">Placement *</CFormLabel>
            <CMultiSelect
              key={placementKey}
              id="placement"
              options={PLACEMENT_OPTIONS}
              selectionType="tags"
              name="placement"
              value={formik.values.placement || []}
              onChange={(value) => formik.setFieldValue('placement', value)}
              onBlur={() => formik.setFieldTouched('placement', true)}
              className={formik.errors.placement && formik.touched.placement ? 'is-invalid' : ''}
            />
            {formik.errors.placement && formik.touched.placement && (
              <CFormFeedback invalid className="d-block">
                {formik.errors.placement}
              </CFormFeedback>
            )}
          </CCol>

          <CCol xs={12}>
            <hr className="mt-1 mb-0" />
            <h6 className="fw-bold mb-0 mt-3">Optional Details</h6>
          </CCol>
          <CCol xs={6}>
            <CFormLabel htmlFor="cta_text">CTA Text</CFormLabel>
            <CFormInput
              placeholder="CTA Text"
              value={formik.values.cta_text}
              onChange={formik.handleChange}
              id="cta_text"
              name="cta_text"
            />
          </CCol>
          <CCol xs={6}>
            <CFormLabel htmlFor="phone_number">Phone Number</CFormLabel>
            <CFormInput
              type="tel"
              placeholder="Phone Number"
              value={formik.values.phone_number}
              onChange={formik.handleChange}
              id="phone_number"
              name="phone_number"
            />
          </CCol>
          <CCol xs={6}>
            <CFormLabel htmlFor="address">Address</CFormLabel>
            <CFormInput
              placeholder="Address"
              value={formik.values.address}
              onChange={formik.handleChange}
              id="address"
              name="address"
            />
          </CCol>
          <CCol xs={6}>
            <CFormLabel htmlFor="cta_url">CTA Link</CFormLabel>
            <CFormInput
              placeholder="CTA Link"
              value={formik.values.cta_url}
              onChange={formik.handleChange}
              id="cta_url"
              name="cta_url"
            />
          </CCol>
          <CCol xs={3}>
            <CFormLabel htmlFor="primary_color">Primary Color</CFormLabel>
            <CFormInput
              type="color"
              value={formik.values.primary_color || '#000000'}
              onChange={formik.handleChange}
              name="primary_color"
              id="primary_color"
            />
          </CCol>
          <CCol xs={3}>
            <CFormLabel htmlFor="primary_hex">Primary Hex Code</CFormLabel>
            <CFormInput
              type="text"
              name="primary_color"
              id="primary_hex"
              value={formik.values.primary_color || '#000000'}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="#000000"
            />
          </CCol>
          <CCol xs={3}>
            <CFormLabel htmlFor="secondary_color">Secondary Color</CFormLabel>
            <CFormInput
              type="color"
              value={formik.values.secondary_color || '#000000'}
              onChange={formik.handleChange}
              name="secondary_color"
              id="secondary_color"
            />
          </CCol>
          <CCol xs={3}>
            <CFormLabel htmlFor="secondary_hex">Secondary Hex Code</CFormLabel>
            <CFormInput
              type="text"
              name="secondary_color"
              id="secondary_hex"
              value={formik.values.secondary_color || '#000000'}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="#000000"
            />
          </CCol>
          <CCol xs={6}>
            <CFormLabel htmlFor="schedule_start_date">Start Date</CFormLabel>
            <CDatePicker
              locale="en-GB"
              inputDateFormat={formatDisplayDate}
              placeholder="DD/MM/YYYY"
              date={formik.values.schedule_start_date || null}
              onDateChange={(date) => formik.setFieldValue('schedule_start_date', toApiDate(date))}
            />
          </CCol>
          <CCol xs={6}>
            <CFormLabel htmlFor="schedule_end_date">End Date</CFormLabel>
            <CDatePicker
              locale="en-GB"
              inputDateFormat={formatDisplayDate}
              placeholder="DD/MM/YYYY"
              minDate={formik.values.schedule_start_date || undefined}
              date={formik.values.schedule_end_date || null}
              onDateChange={(date) => formik.setFieldValue('schedule_end_date', toApiDate(date))}
            />
          </CCol>
        </CForm>
      </COffcanvasBody>
      <div className="d-flex justify-content-between gap-2 p-3 border-top">
        <CButton color="secondary" variant="outline" onClick={handleClose}>
          Cancel
        </CButton>
        <div className="d-flex gap-2">
          <CLoadingButton
            color="secondary"
            variant="outline"
            loading={loader}
            onClick={() => submitForm(true)}
          >
            Save &amp; Add Another
          </CLoadingButton>
          <CLoadingButton color="primary" loading={loader} onClick={() => submitForm(false)}>
            Create Promo Card
          </CLoadingButton>
        </div>
      </div>
    </COffcanvas>
  )
}

export default AddForm
